import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft,
  BookOpen,
  Play,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Lock,
  X,
  Maximize2
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContentViewerPage() {
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState(null);
  const [purchase, setPurchase] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('cards');
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [fullscreenImage, setFullscreenImage] = useState(null);

  const accessToken = searchParams.get('token');

  useEffect(() => {
    if (accessToken) {
      fetchContent();
    } else {
      setError('No access token provided');
      setLoading(false);
    }
  }, [accessToken]);

  const fetchContent = async () => {
    try {
      const response = await fetch(`${API_URL}/api/store/access/${accessToken}`);
      const data = await response.json();
      
      if (response.ok) {
        setContent(data.content);
        setPurchase(data.purchase);
      } else {
        setError(data.detail || 'Failed to load content');
      }
    } catch (err) {
      setError('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const nextCard = () => {
    if (content?.cards && currentCardIndex < content.cards.length - 1) {
      setCurrentCardIndex(currentCardIndex + 1);
    }
  };

  const prevCard = () => {
    if (currentCardIndex > 0) {
      setCurrentCardIndex(currentCardIndex - 1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#C5A059] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FFFCF5] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border border-[#E2E8F0] p-8 max-w-md w-full text-center">
          <Lock className="w-12 h-12 text-[#C5A059] mx-auto mb-4" />
          <h1 className="font-['Playfair_Display'] text-2xl font-bold text-[#0F172A] mb-2">
            Access Denied
          </h1>
          <p className="text-[#64748B] mb-6">{error}</p>
          <Link to="/store">
            <Button className="bg-[#C5A059] hover:bg-[#B8934E] text-white">
              Browse Store
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const hasVideos = purchase?.tier === 'cards_videos' && content?.videos?.length > 0;

  return (
    <div className="min-h-screen bg-[#0F172A]">
      {/* Header */}
      <header className="bg-[#0F172A] border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/store" className="flex items-center gap-2 text-white/70 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Store</span>
          </Link>
          <div className="text-center">
            <h1 className="text-white font-semibold">{content?.topic_name}</h1>
            <p className="text-white/60 text-sm">{content?.grade} • {content?.tier}</p>
          </div>
          <div className="w-20" />
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="bg-[#1E293B] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('cards')}
              className={`px-6 py-3 text-sm font-medium transition-colors flex items-center gap-2 ${
                activeTab === 'cards'
                  ? 'text-[#C5A059] border-b-2 border-[#C5A059]'
                  : 'text-white/60 hover:text-white'
              }`}
            >
              <BookOpen className="w-4 h-4" />
              Revision Cards ({content?.cards?.length || 0})
            </button>
            {hasVideos && (
              <button
                onClick={() => setActiveTab('videos')}
                className={`px-6 py-3 text-sm font-medium transition-colors flex items-center gap-2 ${
                  activeTab === 'videos'
                    ? 'text-[#C5A059] border-b-2 border-[#C5A059]'
                    : 'text-white/60 hover:text-white'
                }`}
              >
                <Play className="w-4 h-4" />
                Videos ({content?.videos?.length || 0})
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {activeTab === 'cards' && content?.cards?.length > 0 && (
          <div>
            {/* Card Viewer */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-xl">
              {/* Card Image */}
              <div 
                className="relative bg-[#F8FAFC] min-h-[400px] flex items-center justify-center cursor-pointer"
                onClick={() => setFullscreenImage(content.cards[currentCardIndex])}
              >
                <img
                  src={content.cards[currentCardIndex]?.url || content.cards[currentCardIndex]}
                  alt={`Card ${currentCardIndex + 1}`}
                  className="max-w-full max-h-[600px] object-contain"
                />
                <button className="absolute top-4 right-4 p-2 bg-black/50 rounded-lg text-white hover:bg-black/70">
                  <Maximize2 className="w-5 h-5" />
                </button>
              </div>

              {/* Navigation */}
              <div className="p-4 bg-white border-t border-[#E2E8F0] flex items-center justify-between">
                <Button
                  onClick={prevCard}
                  disabled={currentCardIndex === 0}
                  variant="outline"
                  className="border-[#E2E8F0]"
                >
                  <ChevronLeft className="w-5 h-5 mr-1" />
                  Previous
                </Button>
                
                <div className="text-center">
                  <span className="text-[#0F172A] font-semibold">
                    {currentCardIndex + 1} / {content.cards.length}
                  </span>
                  {content.cards[currentCardIndex]?.title && (
                    <p className="text-sm text-[#64748B]">
                      {content.cards[currentCardIndex].title}
                    </p>
                  )}
                </div>
                
                <Button
                  onClick={nextCard}
                  disabled={currentCardIndex === content.cards.length - 1}
                  variant="outline"
                  className="border-[#E2E8F0]"
                >
                  Next
                  <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
              </div>
            </div>

            {/* Card Thumbnails */}
            <div className="mt-6 flex gap-2 overflow-x-auto pb-4">
              {content.cards.map((card, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentCardIndex(idx)}
                  className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                    currentCardIndex === idx 
                      ? 'border-[#C5A059] ring-2 ring-[#C5A059]/30' 
                      : 'border-white/20 hover:border-white/40'
                  }`}
                >
                  <img
                    src={card?.url || card}
                    alt={`Thumbnail ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'videos' && hasVideos && (
          <div className="grid md:grid-cols-2 gap-6">
            {content.videos.map((video, idx) => (
              <div key={idx} className="bg-white rounded-xl overflow-hidden">
                <div className="aspect-video bg-black">
                  {video.url?.includes('youtube') || video.url?.includes('youtu.be') ? (
                    <iframe
                      src={video.url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                      className="w-full h-full"
                      allowFullScreen
                      title={video.title}
                    />
                  ) : (
                    <video
                      src={video.url}
                      controls
                      className="w-full h-full"
                    />
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-[#0F172A]">
                    {video.title || `Video ${idx + 1}`}
                  </h3>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* No Videos Message */}
        {activeTab === 'videos' && !hasVideos && (
          <div className="text-center py-20">
            <Lock className="w-12 h-12 text-white/30 mx-auto mb-4" />
            <h3 className="text-white font-semibold mb-2">Videos Not Included</h3>
            <p className="text-white/60">
              Upgrade to Cards + Videos bundle to access walkthrough videos.
            </p>
          </div>
        )}
      </div>

      {/* Fullscreen Image Modal */}
      {fullscreenImage && (
        <div 
          className="fixed inset-0 z-50 bg-black flex items-center justify-center"
          onClick={() => setFullscreenImage(null)}
        >
          <button className="absolute top-4 right-4 p-2 text-white hover:text-[#C5A059]">
            <X className="w-8 h-8" />
          </button>
          <img
            src={fullscreenImage?.url || fullscreenImage}
            alt="Fullscreen"
            className="max-w-full max-h-full object-contain p-4"
          />
        </div>
      )}
    </div>
  );
}
